import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { restaurantConfig } from '../config/restaurantConfig';

import ptFlagSvg from '../assets/images/flags/pt.svg';
import ukFlagSvg from '../assets/images/flags/uk.svg';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  logo: string;
  language: string;
  toggleLanguage: () => void;
  handleReservation: () => void;
}

const DrawerNuclear: React.FC<DrawerProps> = ({ 
  isOpen, 
  onClose, 
  logo, 
  language, 
  toggleLanguage,
  handleReservation
}) => {
  // Close on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleLinkClick = () => onClose();

  const scrollToSection = (sectionId: string) => {
    onClose();
    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const section = document.getElementById(sectionId);
      if (section) section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Inline styles - NUCLEAR approach
  const styles = {
    backdrop: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 99999998,
      opacity: isOpen ? 1 : 0,
      visibility: isOpen ? 'visible' as const : 'hidden' as const,
      transition: 'opacity 0.3s ease',
    },
    drawer: {
      position: 'fixed' as const,
      top: 0,
      right: 0,
      width: '280px',
      maxWidth: '85vw',
      height: '100vh',
      backgroundColor: '#ffffff',
      zIndex: 99999999,
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      flexDirection: 'column' as const,
      overflowY: 'auto' as const,
    },
    closeBtn: {
      position: 'absolute' as const,
      top: '16px',
      right: '16px',
      width: '32px',
      height: '32px',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      color: '#ffffff',
      zIndex: 10,
    },
    logoContainer: {
      padding: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#6f1617',
      borderBottom: 'none',
    },
    logoImg: {
      height: '70px',
      width: 'auto',
      objectFit: 'contain' as const,
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
    },
    nav: {
      padding: '16px 0',
      borderBottom: '1px solid #eee',
    },
    navList: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    navItem: {
      borderBottom: '1px solid #f5f5f5',
    },
    navLink: {
      display: 'block',
      padding: '14px 24px',
      color: '#333',
      textDecoration: 'none',
      fontSize: '15px',
      fontWeight: 600,
      letterSpacing: '0.5px',
      transition: 'background 0.2s, color 0.2s',
    },
    languageSection: {
      padding: '16px 24px',
      borderBottom: '1px solid #eee',
    },
    languageTitle: {
      fontSize: '12px',
      color: '#999',
      marginBottom: '12px',
      textTransform: 'uppercase' as const,
      letterSpacing: '1px',
    },
    flagsRow: {
      display: 'flex',
      gap: '12px',
    },
    flagBtn: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      overflow: 'hidden',
      border: '2px solid #ddd',
      cursor: 'pointer',
      transition: 'border-color 0.2s, transform 0.2s',
      padding: 0,
      background: 'none',
    },
    flagBtnActive: {
      borderColor: '#6f1617',
      transform: 'scale(1.1)',
    },
    flagImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
    infoSection: {
      padding: '20px 24px',
      flex: 1,
    },
    infoTitle: {
      fontSize: '14px',
      fontWeight: 700,
      color: '#6f1617',
      marginBottom: '12px',
      textTransform: 'uppercase' as const,
    },
    infoText: {
      fontSize: '14px',
      color: '#555',
      lineHeight: 1.6,
      marginBottom: '8px',
    },
    infoLink: {
      color: '#6f1617',
      textDecoration: 'none',
    },
    reserveBtn: {
      display: 'block',
      width: 'calc(100% - 48px)',
      margin: '16px 24px 24px',
      padding: '14px 20px',
      backgroundColor: '#6f1617',
      color: '#fff',
      border: 'none',
      borderRadius: '30px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      textAlign: 'center' as const,
      transition: 'background 0.2s, transform 0.2s',
    },
  };

  return (
    <>
      {/* Backdrop */}
      <div style={styles.backdrop} onClick={onClose} />

      {/* Drawer Panel */}
      <div style={styles.drawer}>
        {/* Close Button */}
        <button style={styles.closeBtn} onClick={onClose} aria-label="Close menu">
          ✕
        </button>

        {/* Logo */}
        <div style={styles.logoContainer}>
          <Link to="/" onClick={handleLinkClick}>
            <img src={logo} alt={restaurantConfig.name} style={styles.logoImg} />
          </Link>
        </div>

        {/* Navigation */}
        <nav style={styles.nav}>
          <ul style={styles.navList}>
            <li style={styles.navItem}>
              <Link to="/" onClick={handleLinkClick} style={styles.navLink}>
                {language === 'en' ? 'HOME' : 'INÍCIO'}
              </Link>
            </li>
            <li style={styles.navItem}>
              <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} style={styles.navLink}>
                {language === 'en' ? 'ABOUT US' : 'SOBRE NÓS'}
              </a>
            </li>
            <li style={styles.navItem}>
              <Link to="/menu-full" onClick={handleLinkClick} style={styles.navLink}>
                MENU
              </Link>
            </li>
            <li style={styles.navItem}>
              <a href="#gallery" onClick={(e) => { e.preventDefault(); scrollToSection('gallery'); }} style={styles.navLink}>
                {language === 'en' ? 'GALLERY' : 'GALERIA'}
              </a>
            </li>
            <li style={styles.navItem}>
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} style={styles.navLink}>
                {language === 'en' ? 'CONTACT' : 'CONTACTO'}
              </a>
            </li>
          </ul>
        </nav>

        {/* Language Switcher */}
        <div style={styles.languageSection}>
          <div style={styles.languageTitle}>{language === 'en' ? 'Language' : 'Idioma'}</div>
          <div style={styles.flagsRow}>
            <button 
              style={{...styles.flagBtn, ...(language === 'en' ? styles.flagBtnActive : {})}}
              onClick={() => { localStorage.setItem('language', 'en'); toggleLanguage(); }}
              title="English"
            >
              <img src={ukFlagSvg} alt="EN" style={styles.flagImg} />
            </button>
            <button 
              style={{...styles.flagBtn, ...(language === 'pt' ? styles.flagBtnActive : {})}}
              onClick={() => { localStorage.setItem('language', 'pt'); toggleLanguage(); }}
              title="Português"
            >
              <img src={ptFlagSvg} alt="PT" style={styles.flagImg} />
            </button>
          </div>
        </div>

        {/* Contact Info */}
        <div style={styles.infoSection}>
          <div style={styles.infoTitle}>{language === 'en' ? 'Visit Us' : 'Visite-nos'}</div>
          <p style={styles.infoText}>
            {language === 'en' ? restaurantConfig.address.en : restaurantConfig.address.pt}
          </p>
          <p style={styles.infoText}>
            <a href={`tel:${restaurantConfig.phone}`} style={styles.infoLink}>
              {restaurantConfig.phone}
            </a>
          </p>
          <p style={styles.infoText}>
            <a href={`mailto:${restaurantConfig.email}`} style={styles.infoLink}>
              {restaurantConfig.email}
            </a>
          </p>
        </div>

        {/* Reserve Button */}
        <button 
          style={styles.reserveBtn}
          onClick={() => { handleLinkClick(); scrollToSection('booking'); }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#8a1d1f'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#6f1617'; }}
        >
          {language === 'en' ? 'Reserve a Table' : 'Reservar Mesa'}
        </button>
      </div>
    </>
  );
};

export default DrawerNuclear;
